import { Form, Stack } from 'react-bootstrap';
import { Card, FormattedMessageDiv, FormInput, Navigation, useWizardContext } from '@odmonorepo/core-components';
import { useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bankDetailsOpts, getCheckoutState, updateBankInfoOpts, validateBankOpts } from 'merchant-api/checkout';
import checkDesktopSVG from '../../assets/check-image-desktop.svg';
import checkMobileSVG from '../../assets/check-image-mobile.svg';
import { AppError } from 'merchant-api';
import { useEffect } from 'react';

export const BankDataForm = ({ selectedOffer, applicationId }) => {
  const intl = useIntl();
  const { watch, trigger, setError, setValue } = useFormContext();
  const { checkoutData, query, mutate: checkoutStateMutation } = getCheckoutState();
  const { nextStep } = useWizardContext();
  const { mutate: updateBankInfoMutation } = useMutation({ ...updateBankInfoOpts(), onSuccess: nextStep });
  const { mutate: getBankDetails, data: bankDetails } = useMutation({
    ...bankDetailsOpts(),
    onError: () => { throw AppError.BankDetailsError;},
    onSettled: (bankDetails) => {
      if (bankDetails === undefined || Object.keys(bankDetails).length === 0) {
        setError('routingNumber', {
          message: intl.formatMessage({ id: 'formInput.routingNumber.invalid' }),
          type: 'routingNumberError',
        });
      }
    },
  });

  const { mutate: validateAccountNumber } = useMutation({
    ...validateBankOpts(),
    onError: () =>  { throw AppError.BankDetailsError },
    onSuccess: async (data) => {
      if (data.isValid) {
        if (!checkoutData) await query.refetch();
        if (!checkoutData) throw new Error('Checkout data is undefined');
        if (!bankDetails) throw new Error('Bank details are undefined');
        updateBankInfoMutation({
          checkoutUuid: checkoutData?.checkoutUuid,
          bankName: bankDetails?.name,
          routingNumber: watch('routingNumber'),
          accountNumber: watch('accountNumber'),
        });
        await checkoutStateMutation();
      }
      if (!data.isValid) {
        setError('accountNumber', {
          message: intl.formatMessage({ id: 'formInput.accountNumber.invalid' }),
          type: 'accountNumberError',
        });
      }
    },
  });

  return (
    <Stack gap={4} style={{ width: '100%', maxWidth: '600px' }} className="mx-auto">
      <FormattedMessageDiv id="bankData.title" className="h3 text-center" dataTestId="bankDataTitle" />
      <Card style={{ maxWidth: '600px' }} className="mx-auto" bodyClassName="p-md-6 px-md-8 p-5">
        <Stack gap={5} className="text-start">
          <FormattedMessageDiv id="bankData.subtitle" className="h4 mb-0" />
          <FormattedMessageDiv id="bankData.instructions" />
          <div>
            <FormInput
              name="routingNumber"
              data-testid="routingNumber"
              useYupErrors
              onBlur={() => {
                setValue('routingNumber', watch('routingNumber').trim());
                trigger('routingNumber').then((valid) => valid && getBankDetails(watch('routingNumber')));
              }}
            />
            {bankDetails !== undefined && Object.keys(bankDetails).length > 0 && (
              <Form.Control.Feedback type="valid" className={'text-start'} style={{ display: 'block' }}>
                {bankDetails.name}
                <br />
                {bankDetails.address} {bankDetails.city}, {bankDetails.state} {bankDetails.zipCode}
              </Form.Control.Feedback>
            )}
          </div>
          <FormInput
            name="accountNumber"
            useYupErrors
            data-testid="accountNumber"
            onBlur={async () => {
              setValue('accountNumber', watch('accountNumber').trim());
              await trigger('accountNumber');
            }}
          />
          <FormInput
            name="accountNumberConfirmation"
            useYupErrors
            className="label-expanded"
            data-testid="accountNumberConfirmation"
            onBlur={async () => {
              setValue('accountNumberConfirmation', watch('accountNumberConfirmation').trim());
              await trigger('accountNumberConfirmation');
            }}
          />
          <img src={checkMobileSVG} className="mb-auto d-block d-md-none" alt={intl.formatMessage({ id: 'bankData.checkAltText' })} />
          <img src={checkDesktopSVG} className="mb-auto d-none d-md-block" alt={intl.formatMessage({ id: 'bankData.checkAltText' })} />
        </Stack>
      </Card>
      {selectedOffer && <FormattedMessageDiv className="fs-8" id={`bankData.${selectedOffer.product}.disclaimer`} />}
      <Navigation continueOnly handleNext={async () => await validateAccountNumber({ accountNumber: watch('accountNumber'), applicationId: applicationId })} />
    </Stack>
  );
};
